//  Imported under its real name: despite the `use` prefix it is a factory, not a React hook, and
//  calling it as one in a class is what makes rules-of-hooks fire.
import createApiClient from '../interceptor';
import { apiPath } from './constants';
import { baseApiUrl, keycloakAuthUrl } from 'src/config';
import axios from 'axios';
import tokenManagementService from '../tokenManagementService/tokenService';
import { secretKey } from 'src/config';
import { therapistToken, tokenType } from '../tokenManagementService/constant';

const formatDataToUrlEncoded = (data: any) => {
  const urlEncodedData = new URLSearchParams();
  for (const key in data) {
    urlEncodedData.append(key, data[key]);
  }
  return urlEncodedData.toString();
};

const getToken = () => {
  return tokenManagementService.getToken(therapistToken, secretKey);
};

class AuthService {
  private axiosInstance;

  constructor() {
    this.axiosInstance = this.setupAxiosInstance();
  }

  private setupAxiosInstance() {
    const axiosInstance = createApiClient();
    return axiosInstance;
  }

  therapistLogin = (payload: object, subdomain: string) => {
    const data = formatDataToUrlEncoded(payload);

    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    return axios.post(
      `${keycloakAuthUrl}/${subdomain}/protocol/openid-connect/token`,
      data,
      config
    );
  };

  logout = (payload: object, subdomain: string) => {
    const data = formatDataToUrlEncoded(payload);
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `${tokenType} ${getToken()}`,
      },
    };

    return axios.post(
      `${keycloakAuthUrl}/${subdomain}/protocol/openid-connect/logout`,
      data,
      config
    );
  };

  setPassword = (payload: any) => {
    if (payload.self) {
      const requestData = {
        oldPassword: payload.oldPassword,
        newPassword: payload.newPassword,
      };
      return this.axiosInstance.post(apiPath.SELF_RESET_PASSWORD_THERAPIST_ROLE, requestData, {
        headers: {
          Authorization: `Bearer ${payload.token}`,
        },
      });
    } else {
      return this.axiosInstance.post(apiPath.SET_PASSWORD, payload);
    }
  };

  verifyEmailLink = (data: object) => {
    return this.axiosInstance.get(apiPath.VERIFY_VERIFICATION_LINK, {
      params: { ...data },
    });
  };
  getGeneratedPassword = () => {
    return this.axiosInstance.get(`${apiPath.GENERATE_PASSWORD}`);
  };

  getPostTherapistResetPassword = (payload: object) => {
    return this.axiosInstance.post(`${apiPath.POST_RESET_PASSWORD_THERAPIST}`, payload);
  };

  forgotPassword = async (payload: { email: string; subdomain: string }) => {
    // APIT-696 — deliberately not this.axiosInstance: the caller is signed out, and
    // interceptor.tsx's getAccessToken() never settles its promise while unauthenticated (D54 in
    // TESTING-FINDINGS.md), which would hang this request forever. Same reasoning as
    // therapistLogin/logout above. user-service always returns the same response whether or not
    // the email belongs to an account (anti-enumeration) — callers should not branch on the result.
    //
    // BRK-2181 — fetch rather than bare axios so the global fetch wrapper adds X-Client-IP. Throws
    // on a non-2xx like axios did, so the caller's catch still sees failures.
    const response = await fetch(`${baseApiUrl}${apiPath.FORGOT_PASSWORD}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`Forgot password request failed (HTTP ${response.status})`);
    }
    return response;
  };

  completeForgotPassword = () => {
    // APIT-696 — authenticated: the caller has a fresh Keycloak session from the emailed magic
    // link, so the normal interceptor-backed instance is correct here.
    return this.axiosInstance.post(apiPath.FORGOT_PASSWORD_COMPLETE);
  };
}

const authService = new AuthService();

Object.freeze(authService);

export default authService;
